// src/pages/MADashboardPage.js

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { mentoringApi } from '../axiosInstance';
import { ensureMentoringSession } from '../lib/session';

const MOODS = [
  { value: 1, label: 'Sehr schlecht', color: '#EF4444' },
  { value: 2, label: 'Schlecht',      color: '#F59E0B' },
  { value: 3, label: 'Geht so',       color: '#EAB308' },
  { value: 4, label: 'Gut',           color: '#22C55E' },
  { value: 5, label: 'Sehr gut',      color: '#10B981' },
];

const fmtDE = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function MADashboardPage() {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [loading, setLoading] = useState(true);
  const [surveyStatus, setSurveyStatus] = useState(null);
  const [surveyLoading, setSurveyLoading] = useState(true);
  const [surveyError, setSurveyError] = useState(false);

  // Check-in states
  const [checkinStatus, setCheckinStatus] = useState(null);
  const [checkinLoading, setCheckinLoading] = useState(true);
  const [checkinError, setCheckinError] = useState(false);
  const [selectedMood, setSelectedMood] = useState(null);
  const [comment, setComment] = useState('');
  const [confidenceRating, setConfidenceRating] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null); // { type: 'ok'|'err', text }

  // History states
  const [historyItems, setHistoryItems] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [expandedIds, setExpandedIds] = useState({});

  // Load user info + guard: redirect non-employee to /ma-login
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const whoami = await ensureMentoringSession();
        const u = whoami?.user || whoami;
        if (!alive) return;

        if (!u || u.role !== 'employee') {
          navigate('/ma-login', { replace: true });
          return;
        }

        setFirstName(u.first_name || u.firstName || u.firstname || '');
      } catch {
        if (alive) navigate('/ma-login', { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  // Load survey status
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await mentoringApi.get('/mpi/my-survey-status', { withCredentials: true });
        if (alive) setSurveyStatus(res.data);
      } catch {
        if (alive) setSurveyError(true);
      } finally {
        if (alive) setSurveyLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Load check-in status
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await mentoringApi.get('/mpi/checkins/status', { withCredentials: true });
        if (alive) setCheckinStatus(res.data);
      } catch {
        if (alive) setCheckinError(true);
      } finally {
        if (alive) setCheckinLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Load check-in history
  const loadHistory = useCallback(async (offset = 0, append = false) => {
    try {
      const res = await mentoringApi.get(`/mpi/checkins/my-history?limit=5&offset=${offset}`, { withCredentials: true });
      const data = res.data;
      if (append) {
        setHistoryItems(prev => [...prev, ...(data.checkins || [])]);
      } else {
        setHistoryItems(data.checkins || []);
      }
      setHistoryTotal(data.total || 0);
    } catch { /* silent */ }
    setHistoryLoading(false);
  }, []);

  useEffect(() => { loadHistory(0); }, [loadHistory]);

  // Submit check-in
  async function handleCheckinSubmit() {
    if (!selectedMood || comment.length < 10 || submitting) return;
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      const res = await mentoringApi.post('/mpi/checkins', { mood_score: selectedMood, comment, confidence_rating: confidenceRating }, { withCredentials: true });
      setCheckinStatus(prev => ({
        ...prev,
        can_submit: false,
        last_checkin: res.data?.checkin || { mood_score: selectedMood, comment, submitted_at: new Date().toISOString() },
        next_window_starts: res.data?.next_window_starts || prev?.next_window_starts,
      }));
      setSubmitMsg({ type: 'ok', text: 'Check-in gespeichert' });
      setSelectedMood(null);
      setComment('');
      setConfidenceRating(null);
      // Reload history
      setHistoryOffset(0);
      loadHistory(0);
    } catch (err) {
      if (err?.response?.status === 409) {
        setSubmitMsg({ type: 'err', text: 'Bereits eingecheckt für dieses Fenster' });
        setCheckinStatus(prev => ({ ...prev, can_submit: false }));
      } else {
        setSubmitMsg({ type: 'err', text: 'Fehler beim Senden. Bitte erneut versuchen.' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleLoadMore() {
    const next = historyOffset + 5;
    setHistoryOffset(next);
    loadHistory(next, true);
  }

  function handleLogout() {
    window.location.replace('/#/ma-login');
    fetch('/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-gray-400 text-sm">Laden…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
        <h1 className="text-lg font-bold tracking-tight">Mein Bereich</h1>
        <button
          onClick={handleLogout}
          className="text-sm text-gray-400 hover:text-white transition"
        >
          Abmelden
        </button>
      </header>

      {/* Content */}
      <main className="flex-1 px-6 py-8 max-w-2xl mx-auto w-full">
        {/* Greeting */}
        <h2 className="text-2xl md:text-3xl font-bold mb-8">
          Willkommen{firstName ? `, ${firstName}` : ''}
        </h2>

        {/* Card 1: Kulturumfrage */}
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 mb-4">
          <h3 className="text-lg font-semibold mb-3">Kulturumfrage</h3>

          {surveyLoading ? (
            <p className="text-gray-400 text-sm">Laden…</p>
          ) : surveyError ? (
            <p className="text-gray-500 text-sm">Status konnte nicht geladen werden</p>
          ) : surveyStatus?.has_submitted ? (
            <>
              <p className="text-green-400 text-sm mb-1">
                ✓ Abgegeben am {fmtDE(surveyStatus.submitted_at)}
              </p>
              {surveyStatus.period && (
                <p className="text-gray-400 text-xs">{surveyStatus.period}</p>
              )}
            </>
          ) : (
            <>
              <p className="text-amber-400 text-sm mb-2">
                Ausstehend{surveyStatus?.period ? ` für ${surveyStatus.period}` : ''}
              </p>
              <p className="text-gray-300 text-sm mb-2">
                Sie erhalten den Umfrage-Link per E-Mail
              </p>
              <p className="text-gray-500 text-xs">
                Ihre Antworten sind anonym
              </p>
            </>
          )}
        </div>

        {/* Card 2: Check-in */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 mb-4">
          <h3 className="text-lg font-semibold mb-1">Check-in</h3>
          <p className="text-gray-400 text-xs mb-4">Alle 2 Wochen</p>

          {checkinLoading ? (
            <p className="text-gray-400 text-sm">Laden…</p>
          ) : checkinError ? (
            <p className="text-gray-500 text-sm">Status konnte nicht geladen werden</p>
          ) : checkinStatus?.can_submit ? (
            /* ── Formular ── */
            <div>
              {/* Mood selector */}
              <div className="flex justify-between mb-4">
                {MOODS.map(m => {
                  const sel = selectedMood === m.value;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setSelectedMood(m.value)}
                      className="flex flex-col items-center gap-1 group"
                    >
                      <div
                        className="w-10 h-10 rounded-full border-2 transition-all"
                        style={{
                          borderColor: sel ? m.color : 'rgba(255,255,255,0.2)',
                          background: sel ? m.color : 'transparent',
                          boxShadow: sel ? `0 0 12px ${m.color}40` : 'none',
                        }}
                      />
                      <span className="text-[10px] text-gray-400 group-hover:text-gray-200 transition">
                        {m.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Confidence rating (optional, feature-flagged) */}
              {checkinStatus?.show_confidence && (
                <div className="mb-4 pt-3 border-t border-slate-700/50">
                  <div className="mb-2">
                    <span className="text-sm text-slate-400">Wie sicher fühle ich mich in meiner täglichen Arbeit?</span>
                    {' '}<span className="text-xs text-slate-500">(optional)</span>
                  </div>
                  <div className="flex justify-between">
                    {MOODS.map((m, i) => {
                      const cLabels = ['Sehr unsicher', '', '', '', 'Sehr sicher'];
                      const sel = confidenceRating === m.value;
                      return (
                        <button
                          key={m.value}
                          type="button"
                          onClick={() => setConfidenceRating(sel ? null : m.value)}
                          className="flex flex-col items-center gap-1 group"
                        >
                          <div
                            className="w-8 h-8 rounded-full border-2 transition-all"
                            style={{
                              borderColor: sel ? m.color : 'rgba(255,255,255,0.2)',
                              background: sel ? m.color : 'transparent',
                              boxShadow: sel ? `0 0 10px ${m.color}40` : 'none',
                            }}
                          />
                          {cLabels[i] && (
                            <span className="text-[9px] text-gray-500 group-hover:text-gray-300 transition">
                              {cLabels[i]}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Comment */}
              <div className="relative mb-4">
                <textarea
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="Was beschäftigt dich gerade?"
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-amber-500 transition"
                />
                <span className="absolute bottom-2 right-3 text-[10px] text-gray-500">
                  {comment.length}/500
                </span>
              </div>

              {/* Submit */}
              <button
                onClick={handleCheckinSubmit}
                disabled={!selectedMood || comment.length < 10 || submitting}
                className={`w-full py-2.5 rounded-lg font-semibold text-sm transition ${
                  !selectedMood || comment.length < 10 || submitting
                    ? 'bg-slate-600 text-gray-400 cursor-not-allowed'
                    : 'bg-amber-500 hover:bg-amber-400 text-black'
                }`}
              >
                {submitting ? 'Wird gesendet…' : 'Check-in absenden'}
              </button>

              {submitMsg && (
                <p className={`text-sm mt-3 ${submitMsg.type === 'ok' ? 'text-green-400' : 'text-red-400'}`}>
                  {submitMsg.text}
                </p>
              )}
            </div>
          ) : (
            /* ── Bestätigung ── */
            <div>
              <p className="text-green-400 text-sm mb-2">✓ Eingecheckt am {fmtDE(checkinStatus?.last_checkin?.submitted_at)}</p>

              {/* Mini mood display */}
              <div className="flex gap-2 mb-3">
                {MOODS.map(m => (
                  <div
                    key={m.value}
                    className="w-6 h-6 rounded-full border"
                    style={{
                      borderColor: m.value === checkinStatus?.last_checkin?.mood_score ? m.color : 'rgba(255,255,255,0.15)',
                      background: m.value === checkinStatus?.last_checkin?.mood_score ? m.color : 'transparent',
                    }}
                  />
                ))}
              </div>

              {checkinStatus?.last_checkin?.confidence_rating != null && (
                <div className="mb-3">
                  <span className="text-xs text-slate-500 block mb-1">Handlungssicherheit</span>
                  <div className="flex gap-1.5">
                    {MOODS.map(m => (
                      <div
                        key={m.value}
                        className="w-5 h-5 rounded-full"
                        style={{
                          border: `1.5px solid ${m.value === checkinStatus.last_checkin.confidence_rating ? m.color : 'rgba(255,255,255,0.12)'}`,
                          background: m.value === checkinStatus.last_checkin.confidence_rating ? m.color : 'transparent',
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {checkinStatus?.next_window_starts && (
                <p className="text-gray-400 text-xs">
                  Nächster Check-in ab {fmtDE(checkinStatus.next_window_starts)}
                </p>
              )}
            </div>
          )}
        </div>

        {/* ── Check-in Historie ── */}
        <div className="mb-6">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Meine Check-ins</h4>

          {historyLoading ? (
            <p className="text-gray-400 text-sm">Laden…</p>
          ) : historyItems.length === 0 ? (
            <p className="text-gray-500 text-sm">Noch keine Check-ins vorhanden.</p>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                {historyItems.map((ci, idx) => {
                  const isExpanded = !!expandedIds[ci.id || idx];
                  const txt = ci.comment || '';
                  const truncated = txt.length > 100;
                  const displayText = isExpanded || !truncated ? txt : txt.slice(0, 100) + '…';
                  const mood = MOODS.find(m => m.value === ci.mood_score);

                  return (
                    <div key={ci.id || idx} className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400 text-xs">{fmtDE(ci.submitted_at)}</span>
                        <div className="flex gap-1.5">
                          {MOODS.map(m => (
                            <div
                              key={m.value}
                              className="w-5 h-5 rounded-full"
                              style={{
                                border: `1.5px solid ${m.value === ci.mood_score ? m.color : 'rgba(255,255,255,0.12)'}`,
                                background: m.value === ci.mood_score ? m.color : 'transparent',
                              }}
                            />
                          ))}
                        </div>
                      </div>
                      {txt && (
                        <p
                          className={`text-gray-300 text-sm leading-relaxed ${truncated ? 'cursor-pointer' : ''}`}
                          onClick={() => truncated && setExpandedIds(prev => ({ ...prev, [ci.id || idx]: !isExpanded }))}
                        >
                          {displayText}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {historyItems.length < historyTotal && (
                <button
                  onClick={handleLoadMore}
                  className="mt-3 text-sm text-amber-400 hover:text-amber-300 transition"
                >
                  Mehr laden
                </button>
              )}
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 py-4 text-center">
        <div className="flex items-center justify-center gap-2 opacity-40">
          <Logo variant="wordmark" size="sm" />
        </div>
      </footer>
    </div>
  );
}
